import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Settings, Layout, Image, Star, Megaphone, Save, 
  ChevronUp, ChevronDown, Eye, Search, X, Plus, 
  Trash2, ExternalLink, RefreshCw, Sparkles, Film, Clock, Rocket
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { API_URL } from '../config/api';
import { FeaturedCreatorsManager, FeaturedArtworksManager } from '../components/FeaturedManagers';
import HeroCarouselManager from '../components/HeroCarouselManager';
import NewReleasesManager from '../components/NewReleasesManager';
import LandingPageManager from '../components/LandingPageManager';

const API = API_URL;

const SiteManager = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('landing');
  const [isAdmin, setIsAdmin] = useState(null); // null = checking, true/false = determined
  
  // Site settings state
  const [homepageSections, setHomepageSections] = useState([]);
  const [featuredBanner, setFeaturedBanner] = useState({
    title: '',
    subtitle: '',
    button_text: '',
    button_link: '',
    image_url: ''
  });
  const [announcementBar, setAnnouncementBar] = useState({
    text: '',
    link: '',
    enabled: false
  });
  const [featuredSeries, setFeaturedSeries] = useState([]);
  
  // Series selector state
  const [allSeries, setAllSeries] = useState([]);
  const [allNovels, setAllNovels] = useState([]);
  const [seriesSearch, setSeriesSearch] = useState('');
  const [showSeriesSelector, setShowSeriesSelector] = useState(false);

  // Check admin access on mount and when auth changes
  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) {
      return;
    }
    
    // Check admin access - check multiple sources
    const token = localStorage.getItem('token');
    if (!token) {
      setIsAdmin(false);
      navigate('/');
      return;
    }
    
    // Check from user context first
    let isAdminUser = user?.role === 'admin' || user?.is_admin;
    
    // Fallback to localStorage if user context is not available
    if (!isAdminUser) {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          isAdminUser = parsedUser?.role === 'admin' || parsedUser?.is_admin;
        } catch (e) {
          console.error('Error parsing stored user:', e);
        }
      }
    }
    
    if (!isAdminUser) {
      setIsAdmin(false);
      navigate('/');
      return;
    }
    
    // User is admin - load settings
    setIsAdmin(true);
    loadSiteSettings();
   
  }, [user, authLoading, navigate]);

  const loadSiteSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/site-settings`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setHomepageSections(response.data.homepage_sections || []);
      setFeaturedBanner(response.data.featured_banner || {
        title: '', subtitle: '', button_text: '', button_link: '', image_url: ''
      });
      setAnnouncementBar(response.data.announcement_bar || {
        text: '', link: '', enabled: false
      });
      
      // Load featured series with details
      const featuredResponse = await axios.get(`${API}/admin/featured-series`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setFeaturedSeries(featuredResponse.data.featured_series || []);
      
      setLoading(false);
    } catch (error) {
      console.error('Failed to load site settings:', error);
      toast.error('Failed to load site settings');
      setLoading(false);
    }
  };

  const loadAllContent = async (searchQuery = '') => {
    try {
      const [seriesRes, novelsRes] = await Promise.all([
        axios.get(`${API}/admin/all-series`, {
          params: { search: searchQuery, limit: 50 },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/admin/all-novels`, {
          params: { search: searchQuery, limit: 50 },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
      ]);
      
      setAllSeries(seriesRes.data.series || []);
      setAllNovels(novelsRes.data.novels || []);
    } catch (error) {
      console.error('Failed to load content:', error);
    }
  };

  // Save homepage sections
  const saveSections = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-settings`, {
        homepage_sections: homepageSections
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Homepage sections saved!');
    } catch (error) {
      console.error('Failed to save sections:', error);
      toast.error('Failed to save sections');
    } finally {
      setSaving(false);
    }
  };

  // Save featured banner
  const saveBanner = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-settings`, {
        featured_banner: featuredBanner
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Featured banner saved!');
    } catch (error) {
      console.error('Failed to save banner:', error);
      toast.error('Failed to save banner');
    } finally {
      setSaving(false);
    }
  };

  // Save announcement bar
  const saveAnnouncement = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-settings`, {
        announcement_bar: announcementBar
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Announcement bar saved!');
    } catch (error) {
      console.error('Failed to save announcement:', error);
      toast.error('Failed to save announcement');
    } finally {
      setSaving(false);
    }
  };

  // Save featured series
  const saveFeaturedSeries = async () => {
    setSaving(true);
    try {
      const seriesData = featuredSeries.map((item, index) => ({
        series_id: item.series_id,
        order: index
      }));
      
      await axios.put(`${API}/admin/featured-series`, seriesData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Featured series saved!');
    } catch (error) {
      console.error('Failed to save featured series:', error);
      toast.error('Failed to save featured series');
    } finally {
      setSaving(false);
    }
  };

  // Section management functions
  const toggleSectionVisibility = (index) => {
    const updated = [...homepageSections];
    updated[index].visible = !updated[index].visible;
    setHomepageSections(updated);
  };

  const moveSectionUp = (index) => {
    if (index === 0) return;
    const updated = [...homepageSections];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    // Update order values
    updated.forEach((section, i) => section.order = i);
    setHomepageSections(updated);
  };

  const moveSectionDown = (index) => {
    if (index === homepageSections.length - 1) return;
    const updated = [...homepageSections];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    // Update order values
    updated.forEach((section, i) => section.order = i);
    setHomepageSections(updated);
  };

  // Featured series management
  const addFeaturedSeries = (series) => {
    // Check if already added
    if (featuredSeries.some(item => item.series_id === series.id)) {
      toast.error('This series is already featured');
      return;
    }
    
    setFeaturedSeries([...featuredSeries, {
      series_id: series.id,
      order: featuredSeries.length,
      series: series
    }]);
    setShowSeriesSelector(false);
    toast.success(`Added "${series.title}" to featured series`);
  };

  const removeFeaturedSeries = (index) => {
    const updated = featuredSeries.filter((_, i) => i !== index);
    updated.forEach((item, i) => item.order = i);
    setFeaturedSeries(updated);
  };

  const moveFeaturedUp = (index) => {
    if (index === 0) return;
    const updated = [...featuredSeries];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    updated.forEach((item, i) => item.order = i);
    setFeaturedSeries(updated);
  };

  const moveFeaturedDown = (index) => {
    if (index === featuredSeries.length - 1) return;
    const updated = [...featuredSeries];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    updated.forEach((item, i) => item.order = i);
    setFeaturedSeries(updated);
  };

  if (authLoading || isAdmin === null || loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center" data-testid="site-manager-loading">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-orange-500" />
          <div className="text-xl">Loading Site Manager...</div>
        </div>
      </div>
    );
  }
  
  // If admin check completed but user is not admin, don't render anything (navigate will happen)
  if (isAdmin === false) {
    return null;
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Settings className="w-8 h-8" />
                Site Manager
              </h1>
              <p className="text-white/80 mt-2">Content settings for homepage blocks</p>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="border-white/30 text-white hover:bg-white/10"
                onClick={() => navigate('/admin/page-builder/homepage')}
              >
                <Layout className="w-4 h-4 mr-2" />
                Edit Layout in Page Builder
              </Button>
              <Button 
                variant="outline" 
                className="border-white/30 text-white hover:bg-white/10"
                onClick={() => navigate('/admin')}
              >
                Back to Admin
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Guidance Banner */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <div className="flex items-start gap-3 px-4 py-3 rounded-lg bg-blue-500/10 border border-blue-500/25" data-testid="site-manager-guidance-banner">
          <Sparkles className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
          <div className="text-sm text-blue-200">
            <span className="font-semibold text-blue-100">These settings feed homepage blocks.</span>{' '}
            Layout changes (block order, visibility, arrangement) are managed in{' '}
            <button 
              onClick={() => navigate('/admin/page-builder/homepage')}
              className="underline text-blue-300 hover:text-blue-100 transition-colors"
            >
              Page Builder
            </button>.
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-zinc-900 border border-zinc-800 mb-8 flex-wrap">
            <TabsTrigger value="landing" className="data-[state=active]:bg-orange-600">
              <Rocket className="w-4 h-4 mr-2" />
              Landing Page
            </TabsTrigger>
            <TabsTrigger value="hero" className="data-[state=active]:bg-orange-600">
              <Film className="w-4 h-4 mr-2" />
              Hero Content
            </TabsTrigger>
            <TabsTrigger value="new-releases" className="data-[state=active]:bg-green-600">
              <Clock className="w-4 h-4 mr-2" />
              New Releases
            </TabsTrigger>
            <TabsTrigger value="banner" className="data-[state=active]:bg-purple-600">
              <Image className="w-4 h-4 mr-2" />
              Banner Content
            </TabsTrigger>
            <TabsTrigger value="featured" className="data-[state=active]:bg-purple-600">
              <Star className="w-4 h-4 mr-2" />
              Featured Content
            </TabsTrigger>
            <TabsTrigger value="announcement" className="data-[state=active]:bg-purple-600">
              <Megaphone className="w-4 h-4 mr-2" />
              Announcement
            </TabsTrigger>
          </TabsList>

          {/* Landing Page Tab */}
          <TabsContent value="landing">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="pt-6">
                <LandingPageManager />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Hero Carousel Tab */}
          <TabsContent value="hero">
            <HeroCarouselManager />
          </TabsContent>

          {/* New Releases Tab */}
          <TabsContent value="new-releases">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Clock className="w-5 h-5 text-green-400" />
                  New Releases Manager
                </CardTitle>
                <CardDescription>
                  Curate and reorder the &ldquo;Newly Released Originals&rdquo; section on the homepage.
                  Toggle between algorithmic (automatic) and curated (manual) modes.
                  <span className="block mt-1 text-blue-400/70">Feeds the <code className="bg-zinc-800 px-1 rounded text-xs">new_releases_rail</code> block in Page Builder.</span>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <NewReleasesManager />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Featured Banner Tab */}
          <TabsContent value="banner">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Image className="w-5 h-5 text-blue-400" />
                  Featured Banner Manager
                </CardTitle>
                <CardDescription>
                  Customize the hero banner content. This data feeds the <code className="bg-zinc-800 px-1 rounded text-xs">cta_banner</code> block in Page Builder.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Banner Preview */}
                <div className="relative aspect-video bg-zinc-800 rounded-lg overflow-hidden">
                  {featuredBanner.image_url ? (
                    <img 
                      src={featuredBanner.image_url} 
                      alt="Banner preview" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-500">
                      <Image className="w-16 h-16" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <div className="absolute bottom-6 left-6 right-6">
                    <h2 className="text-3xl font-bold text-white mb-2">
                      {featuredBanner.title || 'Banner Title'}
                    </h2>
                    <p className="text-lg text-gray-300 mb-4">
                      {featuredBanner.subtitle || 'Banner subtitle goes here'}
                    </p>
                    {featuredBanner.button_text && (
                      <Button className="bg-orange-500 hover:bg-orange-600">
                        {featuredBanner.button_text}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Form Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">
                      Banner Title
                    </label>
                    <Input
                      value={featuredBanner.title}
                      onChange={(e) => setFeaturedBanner({...featuredBanner, title: e.target.value})}
                      placeholder="Welcome to Trap Comix"
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">
                      Banner Subtitle
                    </label>
                    <Input
                      value={featuredBanner.subtitle}
                      onChange={(e) => setFeaturedBanner({...featuredBanner, subtitle: e.target.value})}
                      placeholder="Where creators own their stories"
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">
                      Button Text
                    </label>
                    <Input
                      value={featuredBanner.button_text}
                      onChange={(e) => setFeaturedBanner({...featuredBanner, button_text: e.target.value})}
                      placeholder="Start Reading"
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">
                      Button Link URL
                    </label>
                    <Input
                      value={featuredBanner.button_link}
                      onChange={(e) => setFeaturedBanner({...featuredBanner, button_link: e.target.value})}
                      placeholder="/browse"
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">
                    Banner Image URL
                  </label>
                  <Input
                    value={featuredBanner.image_url || ''}
                    onChange={(e) => setFeaturedBanner({...featuredBanner, image_url: e.target.value})}
                    placeholder="https://example.com/banner.jpg"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                  <p className="text-xs text-zinc-500 mt-1">
                    Recommended: 1920x1080 or 16:9 aspect ratio
                  </p>
                </div>

                <div className="pt-4 border-t border-zinc-800">
                  <Button 
                    onClick={saveBanner} 
                    disabled={saving}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Banner Settings'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Featured Tab - Contains all Featured Managers */}
          <TabsContent value="featured" className="space-y-6">
            <div className="flex items-start gap-3 px-4 py-3 rounded-lg bg-purple-500/10 border border-purple-500/25 mb-2">
              <Star className="w-4 h-4 text-purple-400 shrink-0 mt-0.5" />
              <p className="text-sm text-purple-200">
                Curate featured content here. This data feeds the <code className="bg-zinc-800 px-1 rounded text-xs">featured_grid</code>, <code className="bg-zinc-800 px-1 rounded text-xs">creator_grid</code>, and <code className="bg-zinc-800 px-1 rounded text-xs">art_gallery_grid</code> blocks in Page Builder.
              </p>
            </div>
            
            {/* Featured Creators Manager */}
            <FeaturedCreatorsManager />
            
            {/* Featured Artworks Manager */}
            <FeaturedArtworksManager />
            
            {/* Featured Series Manager */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-400" />
                  Featured Series Manager
                </CardTitle>
                <CardDescription>
                  Select and order series to appear in the &quot;Featured&quot; section on the homepage.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Add Series Button */}
                <Dialog open={showSeriesSelector} onOpenChange={setShowSeriesSelector}>
                  <DialogTrigger asChild>
                    <Button 
                      className="bg-yellow-600 hover:bg-yellow-700"
                      onClick={() => {
                        loadAllContent();
                        setShowSeriesSelector(true);
                      }}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Featured Series
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                      <DialogTitle>Select Series to Feature</DialogTitle>
                    </DialogHeader>
                    
                    {/* Search */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                      <Input
                        value={seriesSearch}
                        onChange={(e) => {
                          setSeriesSearch(e.target.value);
                          loadAllContent(e.target.value);
                        }}
                        placeholder="Search series..."
                        className="pl-10 bg-zinc-800 border-zinc-700 text-white"
                      />
                    </div>
                    
                    {/* Series List */}
                    <div className="flex-1 overflow-y-auto space-y-2 mt-4">
                      <h3 className="text-sm font-medium text-zinc-400 mb-2">Comics</h3>
                      {allSeries.map((series) => (
                        <div 
                          key={series.id}
                          className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg hover:bg-zinc-700 cursor-pointer"
                          onClick={() => addFeaturedSeries(series)}
                        >
                          <div className="flex items-center gap-3">
                            {series.cover_url ? (
                              <img 
                                src={series.cover_url} 
                                alt={series.title}
                                className="w-12 h-16 object-cover rounded"
                              />
                            ) : (
                              <div className="w-12 h-16 bg-zinc-700 rounded flex items-center justify-center">
                                <Image className="w-6 h-6 text-zinc-500" />
                              </div>
                            )}
                            <div>
                              <h4 className="font-medium text-white">{series.title}</h4>
                              <p className="text-sm text-zinc-400">
                                {series.genres?.slice(0, 2).join(', ')}
                              </p>
                            </div>
                          </div>
                          <Badge variant="secondary" className="bg-blue-500/20 text-blue-400">
                            Comic
                          </Badge>
                        </div>
                      ))}
                      
                      <h3 className="text-sm font-medium text-zinc-400 mb-2 mt-4">Novels</h3>
                      {allNovels.map((novel) => (
                        <div 
                          key={novel.id}
                          className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg hover:bg-zinc-700 cursor-pointer"
                          onClick={() => addFeaturedSeries({...novel, type: 'novel'})}
                        >
                          <div className="flex items-center gap-3">
                            {novel.cover_url ? (
                              <img 
                                src={novel.cover_url} 
                                alt={novel.title}
                                className="w-12 h-16 object-cover rounded"
                              />
                            ) : (
                              <div className="w-12 h-16 bg-zinc-700 rounded flex items-center justify-center">
                                <Image className="w-6 h-6 text-zinc-500" />
                              </div>
                            )}
                            <div>
                              <h4 className="font-medium text-white">{novel.title}</h4>
                              <p className="text-sm text-zinc-400">
                                {novel.genres?.slice(0, 2).join(', ')}
                              </p>
                            </div>
                          </div>
                          <Badge variant="secondary" className="bg-purple-500/20 text-purple-400">
                            Novel
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Featured Series List */}
                {featuredSeries.length === 0 ? (
                  <div className="text-center py-12 text-zinc-500">
                    <Star className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No featured series yet</p>
                    <p className="text-sm">Click &quot;Add Featured Series&quot; to get started</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {featuredSeries.map((item, index) => (
                      <div 
                        key={item.series_id}
                        className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg border border-zinc-700"
                      >
                        <div className="flex items-center gap-4">
                          <span className="text-2xl font-bold text-yellow-500 w-8">
                            {index + 1}
                          </span>
                          {item.series?.cover_url ? (
                            <img 
                              src={item.series.cover_url} 
                              alt={item.series?.title}
                              className="w-12 h-16 object-cover rounded"
                            />
                          ) : (
                            <div className="w-12 h-16 bg-zinc-700 rounded flex items-center justify-center">
                              <Image className="w-6 h-6 text-zinc-500" />
                            </div>
                          )}
                          <div>
                            <h4 className="font-medium text-white">{item.series?.title}</h4>
                            <p className="text-sm text-zinc-400">
                              {item.series?.genres?.slice(0, 2).join(', ')}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => moveFeaturedUp(index)}
                            disabled={index === 0}
                            className="text-zinc-400 hover:text-white disabled:opacity-30"
                          >
                            <ChevronUp className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => moveFeaturedDown(index)}
                            disabled={index === featuredSeries.length - 1}
                            className="text-zinc-400 hover:text-white disabled:opacity-30"
                          >
                            <ChevronDown className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFeaturedSeries(index)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="pt-4 border-t border-zinc-800">
                  <Button 
                    onClick={saveFeaturedSeries} 
                    disabled={saving}
                    className="bg-yellow-600 hover:bg-yellow-700"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Featured Series'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Announcement Bar Tab */}
          <TabsContent value="announcement">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Megaphone className="w-5 h-5 text-green-400" />
                  Announcement Bar Manager
                </CardTitle>
                <CardDescription>
                  Create a site-wide announcement bar. Feeds the <code className="bg-zinc-800 px-1 rounded text-xs">announcement_banner</code> block in Page Builder.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Preview */}
                {announcementBar.enabled && announcementBar.text && (
                  <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white py-2 px-4 rounded-lg text-center">
                    <p className="text-sm font-medium">
                      {announcementBar.text}
                      {announcementBar.link && (
                        <a href={announcementBar.link} className="ml-2 underline hover:no-underline">
                          Learn more →
                        </a>
                      )}
                    </p>
                  </div>
                )}

                {/* Toggle */}
                <div className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg">
                  <div>
                    <h3 className="font-medium text-white">Enable Announcement</h3>
                    <p className="text-sm text-zinc-400">Show announcement bar across the site</p>
                  </div>
                  <Switch
                    checked={announcementBar.enabled}
                    onCheckedChange={(checked) => setAnnouncementBar({...announcementBar, enabled: checked})}
                  />
                </div>

                {/* Form Fields */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">
                      Announcement Text
                    </label>
                    <Textarea
                      value={announcementBar.text}
                      onChange={(e) => setAnnouncementBar({...announcementBar, text: e.target.value})}
                      placeholder="🎉 Big news! Check out our new features..."
                      className="bg-zinc-800 border-zinc-700 text-white"
                      rows={2}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">
                      Link URL (Optional)
                    </label>
                    <Input
                      value={announcementBar.link || ''}
                      onChange={(e) => setAnnouncementBar({...announcementBar, link: e.target.value})}
                      placeholder="/announcements/new-features"
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                </div>

                <div className="pt-4 border-t border-zinc-800">
                  <Button 
                    onClick={saveAnnouncement} 
                    disabled={saving}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Announcement'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SiteManager;
